import React, { useEffect, useState } from 'react';
import { fetchEmployeesList, updateEmployee, fetchPortsList, fetchDesignationsList } from './services/api';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Form, Card, Alert, Container, Spinner, Row, Col } from 'react-bootstrap';
import { Employee, Port, Designation } from './types';
import { useAuth } from './AuthContext';

const genderOptions = ['Male', 'Female', 'Other'];

const EditEmployee: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [profileImage, setProfileImage] = useState('');
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [employeeCode, setEmployeeCode] = useState('');
  const [name, setName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [gender, setGender] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [dateOfJoining, setDateOfJoining] = useState('');
  const [port, setPort] = useState<number | ''>('');
  const [designation, setDesignation] = useState<number | ''>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [ports, setPorts] = useState<Port[]>([]);
  const [designations, setDesignations] = useState<Designation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [employeesResponse, portsResponse, designationsResponse] = await Promise.all([
          fetchEmployeesList(),
          fetchPortsList(),
          fetchDesignationsList()
        ]);

        const employees = Array.isArray(employeesResponse.data) ? employeesResponse.data : [];
        const ports = Array.isArray(portsResponse.data) ? portsResponse.data : [];
        const designations = Array.isArray(designationsResponse.data) ? designationsResponse.data : [];

        const found = employees.find((e: Employee) => e.id === Number(id));
        if (!found) throw new Error('Employee not found');

        setEmployee(found);
        setProfileImage(found.profile_image || '');
        setEmployeeCode(found.employee_code);
        setName(found.name);
        setMobileNumber(found.mobile_number);
        setGender(found.gender);
        setPassword(found.password);
        setDateOfBirth(found.date_of_birth);
        setDateOfJoining(found.date_of_joining);
        setPort(found.port);
        setDesignation(found.designation?.id || '');
        setIsActive(found.is_active !== undefined ? found.is_active : true);
        setPorts(ports);
        setDesignations(designations);

      } catch (error) {
        console.error('Error loading employee data:', error);
        setError('Failed to load employee');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Auto-generate employee code when port changes (only if port is changed by user)
  useEffect(() => {
    if (!port) return;
    if (!employee) return;
    if (port === employee.port) {
      setEmployeeCode(employee.employee_code);
      return;
    }
    const selectedPort = ports.find(p => p.id === port);
    if (selectedPort) {
      // Use first 3 characters of port name or default to 'EMP'
      const portCode = selectedPort.name.slice(0, 3).toUpperCase() || 'EMP';
      const randomNum = Math.floor(1000 + Math.random() * 9000);
      setEmployeeCode(`${portCode}-${randomNum}`);
    }
  }, [port, ports, employee]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await updateEmployee(Number(id), {
        profile_image: profileImage || undefined,
        employee_code: employeeCode,
        name,
        mobile_number: mobileNumber,
        gender,
        password,
        date_of_birth: dateOfBirth,
        date_of_joining: dateOfJoining,
        port: Number(port),
        designation: Number(designation),
        is_active: isActive
      });
      setSuccess(true);
      setTimeout(() => navigate('/employees'), 1200);
    } catch (err) {
      console.error('Error updating employee:', err);
      setError('Failed to update employee');
    } finally {
      setSaving(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
        setProfileImage(reader.result as string); // Use data URL for now
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) return <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}><Spinner animation="border" /></div>;
  if (error) return <Container className="p-4 p-md-5"><Alert variant="danger">{error}</Alert></Container>;
  if (!employee) return <Container className="p-4 p-md-5">Employee not found.</Container>;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e0f7fa 0%, #e0ffe0 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Card className="glass-card p-4 shadow-lg border-0" style={{ maxWidth: 540, width: '100%' }}>
          <Card.Body>
            <div className="text-center mb-4">
              <h2 className="fw-bold mb-1" style={{ color: '#156082' }}>Edit Employee</h2>
              <div className="text-muted mb-3">Update the details of this employee</div>
              <div className="d-flex flex-column align-items-center mb-3">
                <label htmlFor="profileImageInput" style={{ cursor: 'pointer' }}>
                  {(profilePreview || profileImage) ? (
                    <img src={profilePreview || profileImage} alt="Preview" className="shadow" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: '50%', border: '3px solid #156082', background: '#fff' }} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center bg-light shadow" style={{ width: 96, height: 96, borderRadius: '50%', border: '3px solid #e8f4f8', fontSize: 36, color: '#156082' }}>
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                  <Form.Control id="profileImageInput" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
                </label>
                <div className="small text-muted mt-2">Click to change profile image</div>
              </div>
            </div>
            <Form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="employeeCode">
                    <Form.Label>Employee Code</Form.Label>
                    <Form.Control type="text" value={employeeCode} readOnly disabled placeholder="Auto-generated" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} required maxLength={50} placeholder="Full name" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="mobileNumber">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control type="text" value={mobileNumber} onChange={e => setMobileNumber(e.target.value)} required maxLength={15} placeholder="Mobile number" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="gender">
                    <Form.Label>Gender</Form.Label>
                    <Form.Select value={gender} onChange={e => setGender(e.target.value)} required>
                      <option value="">Select Gender</option>
                      {genderOptions.map(g => <option key={g} value={g}>{g}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="password">
                    <Form.Label>Password</Form.Label>
                    <Form.Control type="text" value={password} onChange={e => setPassword(e.target.value)} required maxLength={32} placeholder="Password" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dateOfBirth">
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control type="date" value={dateOfBirth} onChange={e => setDateOfBirth(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="dateOfJoining">
                    <Form.Label>Date of Joining</Form.Label>
                    <Form.Control type="date" value={dateOfJoining} onChange={e => setDateOfJoining(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="port">
                    <Form.Label>Location</Form.Label>
                    <Form.Select value={port} onChange={e => setPort(e.target.value === '' ? '' : Number(e.target.value))} required disabled={!!(token && token.designation?.user_type === 'ADMIN')}>
                      <option value="">Select Location</option>
                      {ports.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="designation">
                    <Form.Label>Designation</Form.Label>
                    <Form.Select value={designation} onChange={e => setDesignation(e.target.value === '' ? '' : Number(e.target.value))} required>
                      <option value="">Select Designation</option>
                      {designations.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="isActive">
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      checked={isActive}
                      onChange={e => setIsActive(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      Uncheck to deactivate this employee
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
              {success && <Alert variant="success" className="mt-3">Employee updated!</Alert>}
              <div className="d-flex justify-content-between mt-4">
                <Button type="button" variant="secondary" onClick={() => navigate('/employees')} disabled={saving}>Back to List</Button>
                <Button type="submit" variant="primary" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : 'Save Changes'}</Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default EditEmployee; 